import { defineStore } from "pinia";
import activationService from "@/api/activationService";

export const useActivationStore = defineStore("activation", {
  state: () => ({
    activations: [],
    projects: [],
    unassignedPhoneNumbers: [],
    unassignedEmails: [],
    isLoading: false,
    currentActivation: null,
    totalActivations: 0, // For managing pagination
    currentPage: 1,
    pageSize: 10,
  }),
  actions: {
    // Fetch All Activations
    async fetchActivations(page = 1) {
      this.isLoading = true;
      try {
        const params = {
          page,
          size: this.pageSize,
        };
        const { data } = await activationService.getActivations(params);
        this.activations = data.items || [];
        this.totalActivations = data.total;
        this.currentPage = data.page;
        this.pageSize = data.size;
      } catch (error) {
        console.error("Error fetching activations:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async searchActivations(query, page = 1) {
      this.isLoading = true;
      try {
        const { data } = await activationService.searchActivations(
          query,
          page,
          this.pageSize
        );
        this.activations = data.items || [];
        this.totalActivations = data.total;
        this.currentPage = data.page;
        this.pageSize = data.size;
      } catch (error) {
        console.error("Error searching activations:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Activate Project
    async activateProject(activationData) {
      this.isLoading = true;
      try {
        const { data } = await activationService.activateProject(
          activationData
        );
        // this.activations.push(data); // Add the new activation to the state

        return data;
      } catch (error) {
        console.error("Error activating project:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    // Deactivate Project
    async deactivateProject(projectId) {
      this.isLoading = true;
      try {
        await activationService.deactivateProject(projectId);
        const index = this.activations.findIndex(
          (activation) => activation.project.id === projectId
        );
        if (index !== -1) {
          this.activations[index].is_active = false; // Update the state to reflect deactivation
        }
      } catch (error) {
        console.error("Error deactivating project:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Reset Activation
    async resetActivation(activationData) {
      this.isLoading = true;
      try {
        const { data } = await activationService.resetActivation(
          activationData
        );
        const index = this.activations.findIndex(
          (activation) => activation.project.id === activationData.project.id
        );
        if (index !== -1) {
          this.activations.splice(index, 1, data); // Replace the updated activation
        }
        return data;
      } catch (error) {
        console.error("Error resetting activation:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Delete Project
    async deleteProject(projectId) {
      this.isLoading = true;
      try {
        await activationService.deleteProject(projectId);
        this.activations = this.activations.filter(
          (activation) => activation.project_id !== projectId
        ); // Remove the deleted activation from the state
      } catch (error) {
        console.error("Error deleting project:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Get Specific Activation
    async getActivation(projectId) {
      this.isLoading = true;
      try {
        const { data } = await activationService.getActivation(projectId);
        this.currentActivation = data; // Update the current activation in the state
        return data;
      } catch (error) {
        console.error("Error fetching activation:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Get Unassigned Phone Numbers
    async getUnassignedPhoneNumbers(page = 1, size = 10) {
      this.isLoading = true;
      try {
        const { data } = await activationService.getUnassignedPhoneNumbers(
          page,
          size
        );
        this.unassignedPhoneNumbers = data; // Update state with fetched phone numbers
        return data;
      } catch (error) {
        console.error("Error fetching unassigned phone numbers:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Get Unassigned Emails
    async getUnassignedEmails(page = 1, size = 10) {
      this.isLoading = true;
      try {
        const { data } = await activationService.getUnassignedEmails(
          page,
          size
        );
        this.unassignedEmails = data; // Update state with fetched emails
        return data;
      } catch (error) {
        console.error("Error fetching unassigned emails:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async wipeDB() {
      try {
        await activationService.wipeDatabase();
        // Optionally you might want to refresh the totalProjects
        await this.fetchActivations();
      } catch (err) {
        console.error("Error wiping DB:", err);
        throw err;
      }
    },
  },
});
